// @ts-strict-ignore
import React, { useEffect } from 'react';
import { sortBy } from 'lodash';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import { useParams } from 'react-router-dom';

import { charcoal05, gray, primary } from '../../utils/colors';
import { AuthOverlay } from '../../components/authOverlay';
import { useAppDispatch, useAppSelector } from '../../redux';
import { get as getClass } from '../../redux/actions/class';
import { Text } from '../../components/text';
import { AccessKey } from '../../components/styles';
import { formatName, sortOrder } from '../../utils/user';
import { Student } from '../../../types/routes/class';
import { keyColumn } from '../../utils/class';
import { Button } from '../../components/button';

export const PrintCards: React.FC = () => {
  const debugPrint = false;
  const { classId } = useParams();
  const query = new URLSearchParams(window.location.search);
  const preferClassKey = query.get('key') == 'class' ? true : false;
  const dispatch = useAppDispatch();
  const thisClass = useAppSelector((state) => state.class.classes.find((c) => c.id === parseInt(classId)));
  const fetchingClass = useAppSelector((state) => state.class.fetchingClass);

  useEffect(() => {
    if (thisClass) {
      if (debugPrint) console.log(`*** print cards class ${classId} already loaded`);
    } else {
      dispatch(getClass(parseInt(classId)));
    }
  }, []);

  if (!thisClass) {
    if (fetchingClass) return <AuthOverlay text="Loading class" />;
    else return <Text variant="p">Something went wrong. Please contact support@prismsvr.com</Text>;
  } else {
    const students: Student[] = sortBy<Student>(thisClass.students, sortOrder);

    return (
      <>
        <style>{`
          @page { margin: 0.5in; }
          @media print {
            html.no-scroll { overflow: visible !important; }
            body { margin: 0; padding: 0; }
          }
        `}</style>
        <div className={noPrint}>
          <CardsTools>
            {students.length <= 0 && <Text variant="lg3">Preparing login card document...</Text>}
            {students.length > 0 && (
              <>
                <Text variant="lg3">Your login cards are ready!</Text>
                <PrintButton
                  onClick={() => {
                    window.print();
                  }}
                >
                  Print
                </PrintButton>
              </>
            )}
          </CardsTools>
        </div>
        {students.length > 0 && (
          <CardsContainer>
            {students.map((student) => (
              <StudentCard key={student.id}>
                <StudentName variant="md">{formatName(student)}</StudentName>
                <StyledHr></StyledHr>
                <StudentEmail color={gray} variant="lg3">
                  {/* swap to non-breaking hyphen so emails don't wrap at dashes */}
                  {student.email?.replace(/-/g, '‑')}
                </StudentEmail>
                {student.key && (
                  <AccessKeySizeLimit color={primary} variant="lg3">
                    {keyColumn(thisClass.key && preferClassKey) +
                      ':  ' +
                      (thisClass.key && preferClassKey ? thisClass.key : student.key)}
                  </AccessKeySizeLimit>
                )}
              </StudentCard>
            ))}
          </CardsContainer>
        )}
      </>
    );
  }
};

const noPrint = css`
  @media print {
    display: none;
  }
`;

const StyledHr = styled.hr({
  color: charcoal05,
  margin: 0, // avoid extra page overflow
});

const CardsTools = styled.div({
  display: 'flex',
  padding: '10vh',
  justifyContent: 'center',
  alignContent: 'center',
});

const PrintButton = styled(Button)({
  marginLeft: '2rem',
});

const CardsContainer = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  alignContent: 'flex-start',
  width: '100%',
  boxSizing: 'border-box',
  padding: 0,
  margin: 0,
  '@media screen': {
    display: 'none',
  },
});

const StudentCard = styled.div({
  padding: '1.25rem 1rem',
  width: '48%',
  boxSizing: 'border-box',
  margin: '0 1%',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  pageBreakInside: 'avoid',
  breakInside: 'avoid',
});

const StudentName = styled(Text)({
  whiteSpace: 'normal',
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  textOverflow: 'ellipsis',
});

const AccessKeySizeLimit = styled(AccessKey)({
  width: '100%',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
});

const StudentEmail = styled(AccessKey)({
  width: '100%',
  whiteSpace: 'normal',
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
});
